import { useState, useMemo } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Users, Plus, Pencil, Trash2, Search, Mail, Phone, Building2 } from "lucide-react";

interface Client {
  id: string;
  client_name: string;
  client_email: string | null;
  client_company: string | null;
  client_phone: string | null;
  notes: string | null;
  created_at: string;
}

interface Booking {
  id: string;
  campaign_name: string | null;
  brand_name: string | null;
  email: string | null;
  total_budget: number | null;
  status: string;
}

interface AffiliateClientsTabProps {
  clients: Client[];
  bookings: Booking[];
  onRefresh: () => void;
}

export const AffiliateClientsTab = ({ clients, bookings, onRefresh }: AffiliateClientsTabProps) => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const { toast } = useToast();
  const isRTL = language === 'ar';
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [form, setForm] = useState({
    client_name: "", client_email: "", client_company: "", client_phone: "", notes: "",
  });

  // Aggregate spend/campaigns per client email
  const clientStats = useMemo(() => {
    const map: Record<string, { spend: number; count: number }> = {};
    bookings.forEach(b => {
      const email = b.email?.toLowerCase();
      if (email) {
        if (!map[email]) map[email] = { spend: 0, count: 0 };
        map[email].spend += b.total_budget || 0;
        map[email].count += 1;
      }
    });
    return map;
  }, [bookings]);

  const filteredClients = clients.filter(c => {
    if (!search) return true;
    const q = search.toLowerCase();
    return c.client_name.toLowerCase().includes(q) ||
      c.client_email?.toLowerCase().includes(q) ||
      c.client_company?.toLowerCase().includes(q);
  });

  const openAdd = () => {
    setEditingClient(null);
    setForm({ client_name: "", client_email: "", client_company: "", client_phone: "", notes: "" });
    setDialogOpen(true);
  };

  const openEdit = (c: Client) => {
    setEditingClient(c);
    setForm({
      client_name: c.client_name,
      client_email: c.client_email || "",
      client_company: c.client_company || "",
      client_phone: c.client_phone || "",
      notes: c.notes || "",
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!user || !form.client_name.trim()) return;
    setSaving(true);
    try {
      if (editingClient) {
        const { error } = await supabase
          .from('affiliate_clients')
          .update({
            client_name: form.client_name,
            client_email: form.client_email || null,
            client_company: form.client_company || null,
            client_phone: form.client_phone || null,
            notes: form.notes || null,
          })
          .eq('id', editingClient.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('affiliate_clients')
          .insert({
            affiliate_user_id: user.id,
            client_name: form.client_name,
            client_email: form.client_email || null,
            client_company: form.client_company || null,
            client_phone: form.client_phone || null,
            notes: form.notes || null,
          });
        if (error) throw error;
      }
      toast({ title: isRTL ? "تم الحفظ" : "Saved" });
      setDialogOpen(false);
      onRefresh();
    } catch (err: any) {
      toast({ title: isRTL ? "خطأ" : "Error", description: err.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase.from('affiliate_clients').delete().eq('id', id);
      if (error) throw error;
      toast({ title: isRTL ? "تم الحذف" : "Deleted" });
      onRefresh();
    } catch (err: any) {
      toast({ title: isRTL ? "خطأ" : "Error", description: err.message, variant: "destructive" });
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={isRTL ? "بحث في العملاء..." : "Search clients..."}
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-8 w-[250px]"
          />
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button size="sm" onClick={openAdd}>
              <Plus className="h-4 w-4 mr-1" />
              {isRTL ? "إضافة عميل" : "Add Client"}
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingClient ? (isRTL ? "تعديل العميل" : "Edit Client") : (isRTL ? "إضافة عميل" : "Add Client")}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div><Label>{isRTL ? "اسم العميل" : "Client Name"} *</Label><Input value={form.client_name} onChange={e => setForm(p => ({ ...p, client_name: e.target.value }))} /></div>
              <div><Label>{isRTL ? "البريد الإلكتروني" : "Email"}</Label><Input value={form.client_email} onChange={e => setForm(p => ({ ...p, client_email: e.target.value }))} /></div>
              <div><Label>{isRTL ? "الشركة" : "Company"}</Label><Input value={form.client_company} onChange={e => setForm(p => ({ ...p, client_company: e.target.value }))} /></div>
              <div><Label>{isRTL ? "الهاتف" : "Phone"}</Label><Input value={form.client_phone} onChange={e => setForm(p => ({ ...p, client_phone: e.target.value }))} /></div>
              <div><Label>{isRTL ? "ملاحظات" : "Notes"}</Label><Textarea value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} rows={2} /></div>
              <Button onClick={handleSave} disabled={saving || !form.client_name.trim()} className="w-full">
                {isRTL ? "حفظ" : "Save"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Client Cards */}
      {filteredClients.length === 0 ? (
        <Card>
          <CardContent className="py-12">
            <div className="text-center text-muted-foreground">
              <Users className="h-12 w-12 mx-auto mb-4 opacity-30" />
              <p className="font-medium">{isRTL ? "لا يوجد عملاء" : "No clients found"}</p>
              <p className="text-sm mt-1">{isRTL ? "أضف عملاءك لتتبع حملاتهم" : "Add clients to track their campaigns"}</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredClients.map(c => {
            const stats = clientStats[c.client_email?.toLowerCase() || ''] || { spend: 0, count: 0 };
            return (
              <Card key={c.id} className="hover:shadow-md transition-shadow">
                <CardContent className="pt-5 pb-4">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-semibold">{c.client_name}</h3>
                      {c.client_company && (
                        <p className="text-sm text-muted-foreground flex items-center gap-1 mt-0.5">
                          <Building2 className="h-3 w-3" /> {c.client_company}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(c)}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleDelete(c.id)}>
                        <Trash2 className="h-3.5 w-3.5 text-destructive" />
                      </Button>
                    </div>
                  </div>
                  <div className="space-y-1 text-sm">
                    {c.client_email && (
                      <p className="flex items-center gap-1.5 text-muted-foreground">
                        <Mail className="h-3 w-3" /> {c.client_email}
                      </p>
                    )}
                    {c.client_phone && (
                      <p className="flex items-center gap-1.5 text-muted-foreground">
                        <Phone className="h-3 w-3" /> {c.client_phone}
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2 mt-3 pt-3 border-t">
                    <Badge variant="secondary" className="text-xs">
                      {stats.count} {isRTL ? "حملة" : "campaigns"}
                    </Badge>
                    <Badge variant="outline" className="text-xs">
                      {stats.spend.toLocaleString()} SAR
                    </Badge>
                  </div>
                  {c.notes && (
                    <p className="text-xs text-muted-foreground mt-2 line-clamp-2">{c.notes}</p>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};
